// code-guardian-ignore-duplicates: deliberately-parallel report page — shares the
// filter/table/CSV scaffolding of QuizPerformanceReportPage and ScormProgressReportPage
// by design so the admin reports behave identically. What is NOT shared is the funnel:
// a journey is a gated sequence, so where learners stop is the question it exists to
// answer, and no per-learner table answers it on its own.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Search, Route, AlertTriangle, Clock, EyeOff, Lock } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { InlineLoader } from '../components/loading';
import { downloadCsvLegacy } from '../utils/download';
import { useAuthStore } from '../stores/authStore';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';

type JourneyStatus =
  | 'never_opened' | 'not_started' | 'in_progress' | 'completed';

interface JourneyRow {
  employee_code: string;
  employee_name: string;
  journey_id: string;
  journey_title: string | null;
  journey_audience: string | null;
  total_stages: number | null;
  has_opened: boolean;
  assigned_known: boolean;
  status: JourneyStatus;
  completed_stages: number;
  completed_modules: number;
  overall_score: number;
  current_stage_title: string | null;
  current_stage_order: number | null;
  started_at: string | null;
  completed_at: string | null;
  last_activity: string | null;
  days_since_activity: number | null;
  due_date: string | null;
  is_mandatory: boolean;
  days_overdue: number | null;
  is_stalled: boolean;
}

interface FunnelStage {
  stage_id: string;
  order_index: number;
  title: string;
  unlock_condition: 'sequential' | 'score_based';
  required_score: number | null;
  is_milestone: boolean;
  reached: string | number;
  completed: string | number;
  in_progress: string | number;
  locked: string | number;
}

interface Summary {
  population: number;
  assigned: number;
  opened: number;
  never_opened: number;
  in_progress: number;
  completed: number;
  stalled: number;
  overdue: number;
  mandatory_overdue: number;
}

interface JourneyOption { id: string; title: string }

const statusLabel: Record<JourneyStatus, string> = {
  never_opened: 'Never opened',
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

const statusClass: Record<JourneyStatus, string> = {
  never_opened: 'bg-gray-100 text-gray-600',
  not_started: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
};

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const n = (v: string | number) => (typeof v === 'number' ? v : parseInt(v, 10) || 0);

// Epoch ms, so date columns sort chronologically (null → sinks to the bottom).
const toMs = (v: string | null): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

export default function LmsJourneyReportPage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const projectId = selectedProjectId ?? undefined;

  const [journeys, setJourneys] = useState<JourneyOption[]>([]);
  const [journeyId, setJourneyId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [rows, setRows] = useState<JourneyRow[]>([]);
  const [funnel, setFunnel] = useState<FunnelStage[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [stalledAfter, setStalledAfter] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await lmsAPI.getJourneys({ projectId });
        if (cancelled) return;
        const data = resp.data?.data ?? [];
        setJourneys(data.map((j: { id: string; title: string }) => ({ id: j.id, title: j.title })));
      } catch {
        // A failed journey list only costs the picker; the report itself still loads.
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const fetchReport = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await lmsAPI.getJourneyProgressReport({
        projectId,
        journeyId: journeyId === 'all' ? undefined : journeyId,
        status: statusFilter === 'all' ? undefined : statusFilter,
        q: searchQuery.trim() || undefined,
      });
      const data = resp.data?.data;
      setRows(data?.rows ?? []);
      setFunnel(data?.funnel ?? []);
      setSummary(data?.summary ?? null);
      setStalledAfter(data?.stalled_after_days ?? 7);
    } catch {
      setError('Could not load the journey report. Try again, or narrow the filters.');
      setRows([]); setFunnel([]); setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, journeyId, statusFilter, searchQuery]);

  useEffect(() => {
    const t = setTimeout(fetchReport, searchQuery ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchReport, searchQuery]);

  const funnelMax = useMemo(
    () => funnel.reduce((m, s) => Math.max(m, n(s.reached)), 0),
    [funnel],
  );

  // The stage that loses the most people relative to the one before it. This is the
  // single actionable number on the page, so it is called out rather than left for
  // the reader to compute across bars.
  const biggestDrop = useMemo(() => {
    let worst: { stage: FunnelStage; lost: number } | null = null;
    for (let i = 1; i < funnel.length; i++) {
      const lost = n(funnel[i - 1].reached) - n(funnel[i].reached);
      if (lost > 0 && (!worst || lost > worst.lost)) worst = { stage: funnel[i], lost };
    }
    return worst;
  }, [funnel]);

  const sort = useTableSort<JourneyRow>(rows, {
    employee: (r) => r.employee_name,
    journey: (r) => r.journey_title,
    status: (r) => statusLabel[r.status] ?? r.status,
    // The cell's own text ("03 Stage title"), so one journey's stages sort in sequence.
    stage: (r) =>
      r.current_stage_title
        ? `${r.current_stage_order !== null ? `${String(r.current_stage_order + 1).padStart(2, '0')} ` : ''}${r.current_stage_title}`
        : null,
    stages: (r) => r.completed_stages, // "done/total" sorts on the count shown first
    score: (r) => r.overall_score,
    lastActivity: (r) => toMs(r.last_activity),
    due: (r) => toMs(r.due_date),
  });

  const handleExportCSV = () => {
    const head = [
      'Employee Code', 'Employee', 'Journey', 'Status', 'Current Stage',
      'Stages Done', 'Total Stages', 'Modules Done', 'Score',
      'Started', 'Completed', 'Last Activity', 'Days Idle',
      'Due Date', 'Mandatory', 'Days Overdue',
    ];
    const body = rows.map(r => [
      r.employee_code,
      r.employee_name,
      r.journey_title ?? '',
      statusLabel[r.status] ?? r.status,
      r.current_stage_title ?? '',
      r.completed_stages,
      r.total_stages ?? '',
      r.completed_modules,
      r.overall_score,
      r.started_at ?? '',
      r.completed_at ?? '',
      r.last_activity ?? '',
      r.days_since_activity ?? '',
      r.due_date ?? '',
      r.is_mandatory ? 'Yes' : 'No',
      r.days_overdue ?? '',
    ]);
    const csv = [head, ...body]
      .map(line => line.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadCsvLegacy(`learning-journeys-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const tiles = summary ? [
    { key: 'never', label: 'Never opened', value: summary.never_opened, icon: EyeOff,
      hint: 'Assigned but never once opened', tone: 'text-gray-700' },
    { key: 'stalled', label: 'Stalled', value: summary.stalled, icon: Clock,
      hint: `Started, no movement in ${stalledAfter}+ days`, tone: 'text-amber-700' },
    { key: 'overdue', label: 'Overdue', value: summary.overdue, icon: AlertTriangle,
      hint: `${summary.mandatory_overdue} of them mandatory`, tone: 'text-red-700' },
    { key: 'done', label: 'Completed', value: summary.completed, icon: Route,
      hint: `of ${summary.population} in scope`, tone: 'text-emerald-700' },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Route className="h-7 w-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Journey Progress</h1>
            <p className="text-sm text-gray-500 mt-1">
              Where learners are in each journey — who never opened it, who stalled, and which stage holds them.
            </p>
          </div>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={rows.length === 0 || loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 shrink-0"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <section className="rounded-lg border border-gray-100 bg-white p-6 space-y-6">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="jr-journey" className="block text-xs font-medium text-gray-600 mb-1">Journey</label>
            <select
              id="jr-journey"
              value={journeyId}
              onChange={(e) => setJourneyId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All journeys</option>
              {journeys.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="jr-status" className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              id="jr-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All statuses</option>
              <option value="never_opened">Never opened</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="stalled">Stalled ({stalledAfter}+ days idle)</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label htmlFor="jr-search" className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="jr-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name or employee code"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {tiles.map(t => (
              <div key={t.key} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </div>
                <div className={`mt-1 text-2xl font-semibold tabular-nums ${t.tone}`}>{t.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{t.hint}</div>
              </div>
            ))}
          </div>
        )}

        {/* Funnel */}
        {journeyId !== 'all' && funnel.length > 0 && (
          <div className="rounded-lg border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-900">Stage funnel</h2>
            <p className="text-xs text-gray-500 mt-0.5 mb-4">
              Learners who have reached each stage. A stage row exists from the moment a journey is
              opened, so this counts unlocked stages only — not rows.
            </p>
            <div className="space-y-1.5">
              {funnel.map(s => {
                const reached = n(s.reached);
                const pct = funnelMax > 0 ? (reached / funnelMax) * 100 : 0;
                const isDrop = biggestDrop?.stage.stage_id === s.stage_id;
                return (
                  <div key={s.stage_id} className="grid grid-cols-[2rem_minmax(7rem,12rem)_1fr_7rem] gap-3 items-center">
                    <span className="text-xs text-gray-400 tabular-nums">
                      {String(s.order_index + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm text-gray-700 truncate flex items-center gap-1.5" title={s.title}>
                      {s.title}
                      {s.unlock_condition === 'score_based' && (
                        <Lock className="w-3 h-3 text-amber-500 shrink-0" aria-label={`Score gate: ${s.required_score}%`} />
                      )}
                    </span>
                    <div className="h-6 rounded bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-r ${isDrop ? 'bg-red-500' : 'bg-primary-600'}`}
                        style={{ width: `${Math.max(pct, reached > 0 ? 2 : 0)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 tabular-nums text-right">
                      <span className="font-semibold text-gray-900">{reached}</span>
                      {' · '}{n(s.completed)} done
                    </span>
                  </div>
                );
              })}
            </div>
            {biggestDrop && (
              <p className="mt-4 text-sm text-red-800 bg-red-50 border-l-2 border-red-500 px-3 py-2 rounded-r">
                <strong>{biggestDrop.stage.title}</strong> loses {biggestDrop.lost} learner
                {biggestDrop.lost === 1 ? '' : 's'} from the stage before it
                {biggestDrop.stage.unlock_condition === 'score_based'
                  ? ` — it is a ${biggestDrop.stage.required_score}% score gate, so check whether the pass mark or the content is wrong.`
                  : '.'}
              </p>
            )}
          </div>
        )}

        {journeyId === 'all' && (
          <p className="text-xs text-gray-500">
            Pick a single journey to see its stage funnel — stage 3 of one journey has nothing to do with stage 3 of another.
          </p>
        )}

        {/* Table */}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {loading ? (
          <InlineLoader label="Loading…" className="flex justify-center py-16" />
        ) : rows.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-500">
            No learners match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500">
                  <SortTh label="Employee" sortKey="employee" sort={sort} className="px-4 py-3" />
                  <SortTh label="Journey" sortKey="journey" sort={sort} className="px-4 py-3" />
                  <SortTh label="Status" sortKey="status" sort={sort} className="px-4 py-3" />
                  <SortTh label="Current stage" sortKey="stage" sort={sort} className="px-4 py-3" />
                  <SortTh label="Stages" sortKey="stages" sort={sort} align="right" className="px-4 py-3 text-right" />
                  <SortTh label="Score" sortKey="score" sort={sort} align="right" className="px-4 py-3 text-right" />
                  <SortTh label="Last activity" sortKey="lastActivity" sort={sort} className="px-4 py-3" />
                  <SortTh label="Due" sortKey="due" sort={sort} className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sort.sorted.map(r => (
                  <tr key={`${r.journey_id}-${r.employee_code}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.employee_name}</div>
                      <div className="text-xs text-gray-400 tabular-nums">{r.employee_code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.journey_title}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusClass[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {statusLabel[r.status] ?? r.status}
                      </span>
                      {r.is_stalled && (
                        <span className="ml-1 inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Stalled {r.days_since_activity}d
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.current_stage_title
                        ? <>{r.current_stage_order !== null && <span className="text-gray-400 tabular-nums mr-1">{String(r.current_stage_order + 1).padStart(2, '0')}</span>}{r.current_stage_title}</>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {r.completed_stages}<span className="text-gray-400">/{r.total_stages ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">{r.overall_score}%</td>
                    <td className="px-4 py-3 text-gray-600">{fmtDate(r.last_activity)}</td>
                    <td className="px-4 py-3">
                      {r.due_date ? (
                        <span className={r.days_overdue && r.days_overdue > 0 ? 'text-red-700 font-medium' : 'text-gray-600'}>
                          {fmtDate(r.due_date)}
                          {r.days_overdue && r.days_overdue > 0 ? ` · ${r.days_overdue}d late` : ''}
                          {r.is_mandatory && <span className="ml-1 text-xs text-gray-400">(mandatory)</span>}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
